import {
  isMultileg,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  type Security,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useDefaultAggregation } from '../../providers/DefaultAggregationContext';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { useMarketDataMarketSelectorItemBuilder } from '../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import { buildTradingSymbolDefaultPanelMarkets } from '../MarketSelector/tradingSymbolDefaultMarkets';
import { isMarketSelectorItemSelectable } from '../MarketSelector/utils/items';

export interface MarketDataCardSymbolChangeSelections {
  security: Security;
  selectedAggregation: string | undefined;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
}

export function useMarketDataCardSymbolChangeSelections() {
  const { useNewMarketSelector } = useTradingSettings();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const { defaultAggregationId } = useDefaultAggregation();
  const { aggregationPresets } = useMarketSelectorAggregationPresets();
  const buildMarketDataMarketSelectorItems = useMarketDataMarketSelectorItemBuilder();

  return useCallback(
    (
      symbol: string | undefined,
      includeUnifiedLiquidityMarkets: boolean
    ): MarketDataCardSymbolChangeSelections | undefined => {
      const security = symbol ? securitiesBySymbol.get(symbol) : undefined;
      if (!security) {
        return undefined;
      }

      if (isMultileg(security)) {
        return {
          security,
          selectedMarketAccounts: [MULTILEG_MARKET_ACCOUNT],
          selectedMarkets: [],
          selectedAggregation: undefined,
        };
      }

      const items = buildMarketDataMarketSelectorItems({
        symbol: security.Symbol,
        includeUnifiedLiquidityMarkets,
      });
      const selectableItems = items.filter(isMarketSelectorItemSelectable);

      if (selectableItems.length === 1) {
        const item = selectableItems[0];
        return {
          security,
          selectedMarketAccounts: item.marketAccount ? [item.marketAccount.Name] : [],
          selectedMarkets: item.marketAccount ? [] : [item.market.Name],
          selectedAggregation: undefined,
        };
      }

      const fromDefault = buildTradingSymbolDefaultPanelMarkets({
        nextSymbol: security.Symbol,
        security,
        useNewMarketSelector,
        defaultAggregationId,
        aggregationPresets,
        marketsByName,
        marketAccountsByName,
        marketSelectorItems: items,
      });

      if (fromDefault.aggregation != null || fromDefault.markets.length > 0 || fromDefault.marketAccounts.length > 0) {
        return {
          security,
          selectedMarkets: fromDefault.markets,
          selectedMarketAccounts: fromDefault.marketAccounts,
          selectedAggregation: fromDefault.aggregation,
        };
      }

      return {
        security,
        selectedMarketAccounts: [],
        selectedMarkets: [],
        selectedAggregation: undefined,
      };
    },
    [
      aggregationPresets,
      defaultAggregationId,
      marketAccountsByName,
      marketsByName,
      buildMarketDataMarketSelectorItems,
      securitiesBySymbol,
      useNewMarketSelector,
    ]
  );
}
